// src/api/apiClient.js
import axios from 'axios';

// Backend URL. Cookie is set with domain=localhost in dev so it is sent to both :3000 and :3001.
const baseURL = process.env.REACT_APP_API_URL || 'https://qa-tenantbridge.parablu.com';

const apiClient = axios.create({
    baseURL,
    withCredentials: true,  // Important for cookie-based auth
    timeout: 300000, // 5 minutes
    headers: {
        'Content-Type': 'application/json',
    }
});

// Add request interceptor for error handling and logging
apiClient.interceptors.request.use(
    (config) => {
        // Don't log sensitive requests in production
        if (process.env.NODE_ENV !== 'production') {
            console.log('API Request:', {
                url: config.url,
                method: config.method,
                // Don't log full request body for security
                bodySize: config.data ? JSON.stringify(config.data).length : 0,
            });
        }

        // For streaming endpoints, set additional headers
        if (config.url.includes('/api/approve')) {
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            config.headers['Cache-Control'] = 'no-cache';
            config.responseType = 'stream';
            // Disable axios transformers for raw stream handling
            config.transformRequest = [(data) => data];
            config.transformResponse = [(data) => data];
        }

        return config;
    },
    (error) => {
        console.error('API Request Error:', error.message);
        return Promise.reject(error);
    }
);

// Add response interceptor for global error handling
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // Don't trigger app-wide logout for 401 on License Generator flows - show error in the relevant UI instead
        const url = error.config?.url || '';
        const isLicenseDownloadFlow =
            url.includes('/license-generator/licenses/') &&
            (url.includes('/download-token') || url.includes('/download'));
        const isLicenseGeneratorFlow =
            url.includes('/api/license-generator') || url.includes('/api/clouds/search');

        if (error.response && error.response.status === 401 && !isLicenseDownloadFlow && !isLicenseGeneratorFlow) {
            window.dispatchEvent(new CustomEvent('auth:sessionExpired'));
        }

        // Handle server errors
        if (error.response && error.response.status >= 500) {
            console.error('Server Error:', error.response.data);
        }

        return Promise.reject(error);
    }
);

// Custom function for streaming responses
const fetchWithStream = async (url, options = {}, onChunk) => {
    const controller = new AbortController();
    const signal = controller.signal;

    try {
        const response = await fetch(url, {
            ...options,
            signal,
            headers: {
                ...options.headers,
                'Cache-Control': 'no-cache',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            // Process complete JSON objects
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (line.trim()) {
                    try {
                        const data = JSON.parse(line);
                        if (onChunk) onChunk(data);
                    } catch (e) {
                        console.error('Error parsing stream chunk:', e, 'Line:', line);
                    }
                }
            }
        }

        return { success: true };
    } catch (error) {
        console.error('Stream error:', error);
        controller.abort();
        throw error;
    }
};

// API wrapper functions
export const api = {
    // Auth endpoints
    auth: {
        getSession: () => apiClient.get('/api/auth/session'),
        logout: () => apiClient.post('/api/auth/logout')
    },

    // User endpoints
    user: {
        getRoles: () => apiClient.get('/api/user/roles'),
        getTimezone: () => apiClient.get('/api/user/timezone'),
        updateTimezone: (timezone) => apiClient.put('/api/user/timezone', { timezone }),
        getTimezones: () => apiClient.get('/api/user/timezones'),
        deleteAccount: () => apiClient.delete('/api/user/account')
    },

    // Request endpoints
    requests: {
        getAll: () => apiClient.get('/api/requests'),
        getById: (id) => apiClient.get(`/api/requests/${id}`),
        submit: (formData) => apiClient.post('/api/submit-request', { formData }),
        delete: (requestId) => apiClient.delete(`/api/requests/${requestId}`),
        deleteMultiple: (requestIds) => apiClient.delete('/api/requests', { data: { requestIds } }),

        // Approval endpoints
        approve: {
            // Enhanced streaming implementation
            main: (requestId, approverEmail, onLogCallback) => {
                const url = apiClient.defaults.baseURL + '/api/approve';
                return {
                    request: fetchWithStream(
                        url,
                        {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({ requestId, approverEmail })
                        },
                        onLogCallback
                    ),
                    abort: () => { } // Not needed with fetchWithStream, but kept for API consistency
                };
            }
        },

        // Rejection endpoints
        reject: {
            main: (requestId, rejectionReason, rejectedBy) =>
                apiClient.post('/api/reject', { requestId, rejectionReason, rejectedBy })
        },

    },

    // Email endpoint
    email: {
        send: (type, data, recipientEmail, ccEmails) =>
            apiClient.post('/api/email', { type, data, recipientEmail, ccEmails })
    },

    // Bug/Feedback endpoint
    bugComplaint: {
        submit: (type, subject, description) =>
            apiClient.post('/api/submit-bug-complaint', { type, subject, description })
    },

    // License Generator
    licenseGenerator: {
        logGeneration: (payload) =>
            apiClient.post('/api/license-generator', payload),
        // License generation via backend (uses provisioning credentials from DB)
        generateExternalLicense: (payload) =>
            apiClient.post('/api/license-generator/generate', payload),
        // Fetch all licenses from QA provisioning (for History tab)
        getAll: () =>
            apiClient.get('/api/license-generator/all'),
        // Fetch expiring licenses (Expiring Licenses tab)
        getCloudCapacityAll: () =>
            apiClient.get('/api/license-generator/cloud-capacity/all'),
        // Search expiring licenses by cloud name (Expiring Licenses tab)
        getCloudCapacitySearch: (cloudName) =>
            apiClient.get('/api/license-generator/cloud-capacity/search', { params: { cloudName } }),
        // Download expiring licenses file (Expiring Licenses tab)
        getCloudCapacityDownload: () =>
            apiClient.get('/api/license-generator/cloud-capacity/download', { responseType: 'blob' }),
        // Import expiring licenses file (Expiring Licenses tab)
        importCloudCapacityFile: (file) => {
            const formData = new FormData();
            formData.append('file', file);
            return apiClient.post('/api/license-generator/cloud-capacity/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        },
        // Delete imported cloud capacity row by cloud name
        deleteCloudCapacityByName: (cloudName) =>
            apiClient.delete('/api/license-generator/cloud-capacity/delete', {
                params: { cloudName }
            }),
        // Fetch single license request by ID (View details)
        getRequest: (requestId) =>
            apiClient.get(`/api/license-generator/request/${encodeURIComponent(requestId)}`),
        // Fetch license details by cloud name (License details button)
        getLicenseDetailsByCloudName: (cloudName) =>
            apiClient.get(`/api/license-generator/licenses/${encodeURIComponent(cloudName)}`),
        // View license file (open in new tab; uses cookie auth)
        getLicenseFileViewUrl: (cloudName, fileName) =>
            `${apiClient.defaults.baseURL}/api/license-generator/licenses/${encodeURIComponent(cloudName)}/${encodeURIComponent(fileName)}/view`,
        // Fetch license file as blob for in-app viewer (same cookie auth)
        getLicenseFileViewBlob: (cloudName, fileName) =>
            apiClient.get(
                `/api/license-generator/licenses/${encodeURIComponent(cloudName)}/${encodeURIComponent(fileName)}/view`,
                { responseType: 'blob' }
            ),
        // Download license file as blob (used by Download button; stays in-app)
        downloadLicenseFileBlob: (cloudName, fileName) =>
            apiClient.get(
                `/api/license-generator/licenses/${encodeURIComponent(cloudName)}/${encodeURIComponent(fileName)}/licensedownload`,
                { responseType: 'blob' }
            ),
        // Get one-time token (GET = same as License details; cookie often works). Then download with token in URL (no cookie needed).
        getDownloadToken: (cloudName, fileName) =>
            apiClient.get(
                `/api/license-generator/licenses/${encodeURIComponent(cloudName)}/${encodeURIComponent(fileName)}/download-token`
            ),
        // Download with token in URL so the file request does not need the cookie
        getDownloadUrlWithToken: (cloudName, fileName, downloadToken) => {
            const path = `/api/license-generator/licenses/${encodeURIComponent(cloudName)}/${encodeURIComponent(fileName)}/licensedownload?downloadToken=${encodeURIComponent(downloadToken)}`;
            const base = apiClient.defaults.baseURL || (typeof window !== 'undefined' ? window.location.origin : '');
            return base + path;
        },
        getLicenseFileDownloadUrl: (cloudName, fileName) =>
            `${apiClient.defaults.baseURL}/api/license-generator/licenses/${encodeURIComponent(cloudName)}/${encodeURIComponent(fileName)}/licensedownload`,
        // Search licenses by cloud name (pass search term as cloudName query param)
        getCloudNames: (cloudName) =>
            apiClient.get('/api/license-generator/cloud-names', {
                params: cloudName != null && String(cloudName).trim() ? { cloudName: String(cloudName).trim() } : {}
            })
    },

    // Admin endpoints
    admin: {
        getUsers: () => apiClient.get('/api/admin/users'),
        addUser: (email, roles,
                  categoriesSingleTenancy = [], categoriesMultiTenancy = [],
                  approverCategoriesSingleTenancy = [], approverCategoriesMultiTenancy = [],
                  autoProvisionSingleTenancyEnabled = false, autoProvisionSingleTenancyCategories = [],
                  autoProvisionMultiTenancyEnabled = false, autoProvisionMultiTenancyCategories = [],
                  linodeEnabled = false,
                  preReleaseAccessEnabled = false) =>
            apiClient.post('/api/admin/users', {
                email,
                roles,
                categoriesSingleTenancy,
                categoriesMultiTenancy,
                approverCategoriesSingleTenancy,
                approverCategoriesMultiTenancy,
                autoProvisionSingleTenancyEnabled,
                autoProvisionSingleTenancyCategories,
                autoProvisionMultiTenancyEnabled,
                autoProvisionMultiTenancyCategories,
                linodeEnabled,
                preReleaseAccessEnabled
            }),
        updateUser: (email, roles,
                     categoriesSingleTenancy = [], categoriesMultiTenancy = [],
                     approverCategoriesSingleTenancy = [], approverCategoriesMultiTenancy = [],
                     autoProvisionSingleTenancyEnabled = false, autoProvisionSingleTenancyCategories = [],
                     autoProvisionMultiTenancyEnabled = false, autoProvisionMultiTenancyCategories = [],
                     linodeEnabled = false,
                     preReleaseAccessEnabled = false) =>
            apiClient.put(`/api/admin/users/${encodeURIComponent(email)}`, {
                roles,
                categoriesSingleTenancy,
                categoriesMultiTenancy,
                approverCategoriesSingleTenancy,
                approverCategoriesMultiTenancy,
                autoProvisionSingleTenancyEnabled,
                autoProvisionSingleTenancyCategories,
                autoProvisionMultiTenancyEnabled,
                autoProvisionMultiTenancyCategories,
                linodeEnabled,
                preReleaseAccessEnabled
            }),
        deleteUser: (email) =>
            apiClient.delete(`/api/admin/users/${encodeURIComponent(email)}`),
        getEvents: (limit = 1000, offset = 0) =>
            apiClient.get('/api/admin/events', { params: { limit, offset } }),
        getDecommissionEventDetails: (requestId) =>
            apiClient.get(`/api/admin/events/decommission-details/${encodeURIComponent(requestId)}`),
        deleteAllEvents: () =>
            apiClient.delete('/api/admin/events'),
        deleteLast100Events: () =>
            apiClient.delete('/api/admin/events', { params: { limit: 100 } }),
        createReleaseToast: (title, description, startTime, endTime, icon = 'bell') =>
            apiClient.post('/api/admin/release-toasts', { title, description, startTime, endTime, icon }),
        getReleaseToasts: (limit = 100) =>
            apiClient.get('/api/admin/release-toasts', { params: { limit } }),
        stopReleaseToast: (id) =>
            apiClient.put(`/api/admin/release-toasts/${encodeURIComponent(id)}/stop`),
        deleteReleaseToast: (id) =>
            apiClient.delete(`/api/admin/release-toasts/${encodeURIComponent(id)}`),
        setMaintenanceMode: (enabled) =>
            apiClient.put('/api/admin/system/maintenance-mode', { enabled }),
        setImportCloudEnabled: (enabled) =>
            apiClient.put('/api/admin/system/import-cloud-enabled', { enabled }),
        setDecommissionStepStatusRawEnabled: (enabled) =>
            apiClient.put('/api/admin/system/decommission-step-status-raw-enabled', { enabled }),
        createDropdownOption: (payload) =>
            apiClient.post('/api/admin/dropdown-options', payload),
        updateDropdownOption: (id, payload) =>
            apiClient.put(`/api/admin/dropdown-options/${encodeURIComponent(id)}`, payload),
        deleteDropdownOption: (id) =>
            apiClient.delete(`/api/admin/dropdown-options/${encodeURIComponent(id)}`)
    },

    dropdownOptions: {
        getAll: (groupKey = null, includeInactive = false) =>
            apiClient.get('/api/dropdown-options', {
                params: {
                    ...(groupKey ? { groupKey } : {}),
                    ...(includeInactive ? { includeInactive: true } : {})
                }
            })
    },

    releaseToasts: {
        getCurrent: () =>
            apiClient.get('/api/release-toasts/current')
    },

    system: {
        getMaintenanceMode: () =>
            apiClient.get('/api/system/maintenance-mode'),
        getImportCloudEnabled: () =>
            apiClient.get('/api/system/import-cloud-enabled'),
        getDecommissionStepStatusRawEnabled: () =>
            apiClient.get('/api/system/decommission-step-status-raw-enabled'),
        getMaintenanceAccess: (email) =>
            apiClient.get('/api/system/maintenance-access', { params: { email } })
    },

    multiTenancyDecommission: {
        getActiveClouds: () =>
            apiClient.get('/api/multi-tenancy-decommission/active-clouds'),
        getActiveCloudDetails: (cloudName, options = {}) =>
            apiClient.get(
                `/api/multi-tenancy-decommission/active-clouds/${encodeURIComponent(cloudName)}`,
                {
                    params: options
                }
            ),
        checkSubmitEligibility: (cloudName) =>
            apiClient.get('/api/multi-tenancy-decommission/submit-eligibility', {
                params: { cloudName }
            }),
        submitRequest: (payload) =>
            apiClient.post('/api/multi-tenancy-decommission/request', payload),
        getMyRequests: () =>
            apiClient.get('/api/multi-tenancy-decommission/requests/mine'),
        getPendingSubApprovalRequests: () =>
            apiClient.get('/api/multi-tenancy-decommission/requests/pending/sub-approval'),
        getPendingRequests: () =>
            apiClient.get('/api/multi-tenancy-decommission/requests/pending'),
        getSubApprovalHistory: () =>
            apiClient.get('/api/multi-tenancy-decommission/requests/history/sub-approval'),
        getApprovalHistory: () =>
            apiClient.get('/api/multi-tenancy-decommission/requests/history'),
        getStepStatus: (requestId) =>
            apiClient.get(
                `/api/multi-tenancy-decommission/requests/${encodeURIComponent(requestId)}/step-status`
            ),
        deleteSelectedHistory: (requestIds = []) =>
            apiClient.post('/api/multi-tenancy-decommission/requests/history/delete-selected', {
                requestIds
            }),
        deleteAllHistory: () =>
            apiClient.delete('/api/multi-tenancy-decommission/requests/history'),
        subApprove: (payload) =>
            apiClient.post('/api/multi-tenancy-decommission/sub-approve', payload),
        approve: (payload) =>
            apiClient.post('/api/multi-tenancy-decommission/approve', payload),
        validateFinalApproval: (payload) =>
            apiClient.post('/api/multi-tenancy-decommission/validate-final-approval', payload),
        closeValidityBlock: (payload) =>
            apiClient.post('/api/multi-tenancy-decommission/close-validity-block', payload),
        complete: (payload) =>
            apiClient.post('/api/multi-tenancy-decommission/complete', payload),
        reject: (payload) =>
            apiClient.post('/api/multi-tenancy-decommission/reject', payload)
    },

    // Cloud / customer search (via backend, which talks to QA provisioning API)
    clouds: {
        /**
         * Search for a cloud / customer name.
         * The backend proxies this to the QA provisioning API.
         */
        search: (cloudName = '') =>
            apiClient.get('/api/clouds/search', {
                params: cloudName ? { cloudName } : {}
            })
    }
};

export default apiClient;